import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api } from "../api/client";

const STATUS_LABELS = {
  draft: { label: "Draft", color: "#94afc9", bg: "#e8eef5" },
  in_progress: { label: "In progress", color: "#1e6f3e", bg: "#e6f4ed" },
  questions_submitted: { label: "Submitted", color: "#1e3a5f", bg: "#dde8f3" },
  completed: { label: "Completed", color: "#5a3e8a", bg: "#ede8f7" },
};

function StatusBadge({ status }) {
  const s = STATUS_LABELS[status] || { label: status, color: "#6b8cae", bg: "#e8eef5" };
  return (
    <span style={{
      fontSize: 11, fontWeight: 500, padding: "2px 8px", borderRadius: 20,
      background: s.bg, color: s.color, whiteSpace: "nowrap",
    }}>
      {s.label}
    </span>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { token, user, activeCompany } = useAuth();
  const [inspections, setInspections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [createErr, setCreateErr] = useState("");

  useEffect(() => {
    if (!token) { setLoading(false); return; }
    api("/india/inspections", { token })
      .then((res) => setInspections((res?.items || []).slice(0, 5)))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [token]);

  const greeting = () => {
    const h = new Date().getHours();
    if (h < 12) return "Good morning";
    if (h < 17) return "Good afternoon";
    return "Good evening";
  };

  return (
    <div style={{ maxWidth: 800, margin: "0 auto", width: "100%" }}>

      {/* Page header */}
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ fontSize: 22, fontWeight: 500, color: "#1e3a5f", margin: "0 0 4px" }}>
          {greeting()}{user?.first_name ? `, ${user.first_name}` : ""}
        </h1>
        <p style={{ fontSize: 14, color: "#6b8cae", margin: 0 }}>
          {activeCompany?.name || "Oskar India Tango"}
        </p>
      </div>

      {/* Start inspection card */}
      <div style={{
        background: "#1e3a5f", borderRadius: 14,
        padding: "28px 28px", marginBottom: 16,
        display: "flex", alignItems: "center", justifyContent: "space-between",
        gap: 16, flexWrap: "wrap",
      }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 500, color: "white", marginBottom: 4 }}>
            Start a new inspection
          </div>
          <div style={{ fontSize: 13, color: "rgba(255,255,255,0.55)" }}>
            Create a draft and set up vessel, templates and inspectors
          </div>
        </div>
        <button
          disabled={creating}
          onClick={async () => {
            setCreateErr("");
            setCreating(true);
            try {
              const res = await api("/india/inspections/draft", { token, method: "POST", body: { location_type: "vessel" } });
              navigate(`/inspections/${res.inspection_id}/setup`);
            } catch (e) {
              setCreateErr(e?.message || "Failed to create inspection");
            } finally {
              setCreating(false);
            }
          }}
          style={{
            padding: "10px 22px", background: "#63b3ed", color: "#0c2a4a",
            border: "none", borderRadius: 8, fontSize: 14, fontWeight: 500,
            cursor: creating ? "not-allowed" : "pointer", opacity: creating ? 0.6 : 1,
            whiteSpace: "nowrap", flexShrink: 0,
          }}
        >
          {creating ? "Creating…" : "+ New inspection"}
        </button>
        {createErr && (
          <div style={{ width: "100%", fontSize: 13, color: "#fca5a5" }}>{createErr}</div>
        )}
      </div>

      {/* Recent inspections card */}
      <div style={{
        background: "white", borderRadius: 14,
        border: "1px solid #d0dcea", overflow: "hidden",
      }}>
        <div style={{
          padding: "16px 20px", borderBottom: "1px solid #e8eef5",
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          <h2 style={{ fontSize: 15, fontWeight: 500, color: "#1e3a5f", margin: 0 }}>
            Recent inspections
          </h2>
          <button
            onClick={() => navigate("/inspections")}
            style={{ fontSize: 12, color: "#6b8cae", background: "none", border: "none", cursor: "pointer", padding: 0 }}
          >
            View all →
          </button>
        </div>

        {loading ? (
          <div style={{ padding: "20px", fontSize: 13, color: "#94afc9" }}>Loading…</div>
        ) : inspections.length === 0 ? (
          <div style={{ padding: "32px 20px", textAlign: "center" }}>
            <div style={{ fontSize: 13, color: "#94afc9", marginBottom: 8 }}>No inspections yet</div>
            <div style={{ fontSize: 12, color: "#b0c4d8" }}>Create your first inspection above</div>
          </div>
        ) : (
          <div>
            {inspections.map((insp, i) => (
              <div key={insp.id} style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                padding: "12px 20px", gap: 12,
                borderBottom: i < inspections.length - 1 ? "1px solid #e8eef5" : "none",
              }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 500, color: "#1e3a5f", marginBottom: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}>
                    {insp.vessel_name || insp.name || `Inspection #${insp.id}`}
                  </div>
                  <div style={{ fontSize: 12, color: "#94afc9", display: "flex", alignItems: "center", gap: 8 }}>
                    <StatusBadge status={insp.status} />
                    {insp.open_findings > 0 && (
                      <span style={{ color: "#c0392b" }}>· {insp.open_findings} open point{insp.open_findings !== 1 ? "s" : ""}</span>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => navigate(`/inspections/${insp.id}`)}
                  style={{
                    fontSize: 12, padding: "6px 14px",
                    border: "1px solid #d0dcea", borderRadius: 6,
                    background: "white", color: "#1e3a5f",
                    cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0,
                  }}
                >
                  Open
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}